// Source list helpers shared by the article wizard (selected sources) and the article
// editor (bibliography / external tool evidence). Behavior pinned by sources.test.js.

export const MAX_KEYWORDS = 3;
export const MAX_SOURCES = 5;

export const normalizeSourcesList = (raw) => {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
};

/**
 * State updater used by the Sources step: toggles (or forces) selection of a source by id,
 * never duplicating it and never exceeding `maxSources`.
 */
export const toggleSourceSelection = (prev, source, shouldSelect, maxSources = MAX_SOURCES) => {
  const list = normalizeSourcesList(prev);
  const sourceId = String(source?.id || '');
  const exists = list.some((item) => String(item?.id || '') === sourceId);
  const select = shouldSelect == null ? !exists : Boolean(shouldSelect);

  if (!select) {
    return list.filter((item) => String(item?.id || '') !== sourceId);
  }
  if (exists || list.length >= maxSources) {
    return list;
  }
  return [...list, source];
};

/** Maps Sejm ELI API urls (WDU/WMP ids) to the public ISAP document page. */
export const toPublicSejmUrl = (rawUrl = '') => {
  const url = String(rawUrl || '').trim();
  const match = url.match(/\/eli\/acts\/(WDU\d+|WMP\d+)/i) || url.match(/[?&]id=(WDU\d+|WMP\d+)/i);
  if (match?.[1]) {
    return `https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=${encodeURIComponent(match[1].toUpperCase())}`;
  }
  return url;
};

/** Strips HTML tags/entities and collapses whitespace. */
export const sanitizeSourceText = (value = '') => String(value || '')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&#160;/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\u00a0/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** http(s) url without template placeholders such as {id}. */
export const isBrowseableUrl = (rawUrl = '') => {
  const url = String(rawUrl || '').trim();
  return url.startsWith('http') && !/[<>{}]/.test(url);
};

export const humanizeToolName = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

export const resolveExternalToolSourceDetails = (source, externalToolEvidence = []) => {
  if (source?.type !== 'external_tool') return null;

  const toolName = String(source?.tool_name || source?.title || '').trim();
  const sourceUrl = typeof source?.url === 'string' && source.url.startsWith('http')
    ? toPublicSejmUrl(source.url)
    : null;

  const matching = externalToolEvidence.filter((entry) => String(entry?.tool_name || '').trim() === toolName);
  if (matching.length === 0) {
    return {
      toolNameLabel: humanizeToolName(toolName),
      datasetName: null,
      queryText: sanitizeSourceText(source?.query || ''),
      requestUrl: isBrowseableUrl(sourceUrl) ? sourceUrl : null,
      hasConcreteCandidate: Boolean(sourceUrl),
    };
  }

  const scoreEntry = (entry) => {
    const candidateUrls = Array.isArray(entry?.source_meta?.source_candidates)
      ? entry.source_meta.source_candidates
        .map((candidate) => candidate?.url)
        .filter((url) => typeof url === 'string')
      : [];
    const requestUrl = typeof entry?.source_meta?.request_url === 'string' ? entry.source_meta.request_url : '';
    const resultUrls = Array.isArray(entry?.result_urls) ? entry.result_urls : [];
    const urlMatch = sourceUrl
      ? [requestUrl, ...resultUrls, ...candidateUrls].some((url) => toPublicSejmUrl(url) === sourceUrl || url === source?.url)
      : false;
    const itemsCount = Number(entry?.result_items_count || 0);
    const hasStructuredSample = (() => {
      const sample = entry?.result_sample;
      if (!sample) return false;
      if (Array.isArray(sample)) return sample.length > 0;
      if (typeof sample !== 'object') return false;
      return Object.values(sample).some((value) => {
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') return Object.keys(value).length > 0;
        return Boolean(value);
      });
    })();
    const isSuccess = entry?.success ? 1 : 0;
    return (urlMatch ? 100 : 0) + (itemsCount > 0 ? 20 : 0) + (hasStructuredSample ? 10 : 0) + isSuccess;
  };

  const bestEntry = [...matching].sort((a, b) => scoreEntry(b) - scoreEntry(a))[0] || null;
  const rawRequestUrl = bestEntry?.source_meta?.request_url || sourceUrl || null;
  const requestUrl = isBrowseableUrl(rawRequestUrl) ? rawRequestUrl : null;
  const candidateList = Array.isArray(bestEntry?.source_meta?.source_candidates)
    ? bestEntry.source_meta.source_candidates
    : [];
  const concreteCandidate = candidateList.find((candidate) => {
    const hasTitle = typeof candidate?.title === 'string' && candidate.title.trim().length > 0;
    const hasSourceId = typeof candidate?.source_id === 'string' && candidate.source_id.trim().length > 0;
    const hasDistinctUrl = typeof candidate?.url === 'string'
      && candidate.url.startsWith('http')
      && candidate.url !== requestUrl;
    return hasTitle || hasSourceId || hasDistinctUrl;
  });

  return {
    toolNameLabel: humanizeToolName(toolName),
    datasetName: bestEntry?.source_meta?.dataset_name || null,
    queryText: sanitizeSourceText(source?.query || bestEntry?.query_hint || ''),
    requestUrl,
    publicSourceUrl: sourceUrl,
    hasConcreteCandidate: Boolean(concreteCandidate) || Number(bestEntry?.result_items_count || 0) > 0,
  };
};

/**
 * Link/label data rendered for a cited knowledge-base source in the bibliography.
 */
export const resolveCitedSourceLinks = (source, externalDetails) => {
  const sourceUrl = (() => {
    const raw = externalDetails?.publicSourceUrl || source?.url || null;
    if (typeof raw !== 'string' || !raw.startsWith('http') || /[<>{}]/.test(raw)) return null;
    return raw;
  })();
  const apiPreviewUrl = typeof externalDetails?.requestUrl === 'string'
    && externalDetails.requestUrl.startsWith('http')
    && !/[<>{}]/.test(externalDetails.requestUrl)
    && externalDetails.requestUrl !== sourceUrl
    ? externalDetails.requestUrl
    : null;
  const queryText = String(externalDetails?.queryText || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return { sourceUrl, apiPreviewUrl, queryText };
};

/**
 * articles_linked_source (M2M, possibly expanded) -> list of source objects.
 */
export const parseLinkedSources = (data) => {
  console.log('Raw articles_linked_source from API:', data.articles_linked_source);
  if (data.articles_linked_source && data.articles_linked_source.length > 0) {
    const parsedLinkedSources = data.articles_linked_source.map(item => {
      // Check if it's expanded M2M data
      if (typeof item === 'object' && item.articles_sources_id) {
        return item.articles_sources_id;
      }
      return item;
    }).filter(source => source && typeof source === 'object');

    console.log('Parsed linked sources:', parsedLinkedSources);
    return parsedLinkedSources;
  }
  return [];
};
